import React, { useEffect } from 'react'
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { AnyAction } from 'redux';

import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogAutoCompleteVehicleControlled from '@/main/components/shared/inputAutoComplete/autoCompleteVehicleControlled';

import { KeyValue } from '@/main/models/common';
import { getTranslateByKey, isNullUndefined, isValueQueryParams } from '@/main/services/utils';
import { getManufacturers, resetReducerManufacturers, useManufacturerReducer } from '@/main/store/vehicle/manufacturer';
import { getModels, resetReducerModels, useModelReducer } from '@/main/store/vehicle/model';
import { getSubModels, resetReducerSubModels, useSubModelReducer } from '@/main/store/vehicle/subModel';
import { getYearByManufacturerAndModel, getYearByManufacturerAndModelSaga, getYears, resetReducerYears, useYearReducer } from '@/main/store/vehicle/year';
import { ManufacturerRequest, ModelRequest, SubModelRequest, VehicleYearRequest } from '@/main/models/request';
import { useDinamicFiltersMappingCodes } from './dinamicFiltersMappingCodes';

export const INITIAL_FORM_STATE_VEHICLE = {
  manufacturer: null as KeyValue | null,
  model: null as KeyValue | null,
  subModel: null as KeyValue | null,
  year: null as KeyValue | null,
};

export type IFormFiltersVechile = typeof INITIAL_FORM_STATE_VEHICLE

interface IFiltersVechileProps { }

function FiltersVechile({
}: IFiltersVechileProps) {
  //#region Hooks
  const dispatch = useDispatch();
  const {
    isEdit,
    keyReducer,
    watcherCountry,
    isRequiredField
  } = useDinamicFiltersMappingCodes();
  const {
    control,
    resetField,
    setValue,
    watch
  } = useFormContext<IFormFiltersVechile>();
  const watcherManufacturer = watch('manufacturer');
  const watcherModel = watch('model');
  const watcherSubModel = watch('subModel');
  //#endregion

  //#region Reducers
  const { isolated: manufacturersReducer } = useManufacturerReducer(keyReducer);
  const { isolated: modelsReducer } = useModelReducer(keyReducer);
  const { isolated: subModelsReducer } = useSubModelReducer(keyReducer);
  const { isolated: yearsReducer } = useYearReducer(keyReducer);
  //#endregion

  //#region Core Functions
  function resetReducers(keyFields: (keyof IFormFiltersVechile)[]) {
    const mapReducerField: { [K in keyof IFormFiltersVechile]: (keyReducerAction?: string) => AnyAction } = {
      manufacturer: resetReducerManufacturers,
      model: resetReducerModels,
      subModel: resetReducerSubModels,
      year: resetReducerYears,
    };

    keyFields.forEach((key) => {
      const resetReducerField = mapReducerField[key];
      dispatch(resetReducerField(keyReducer))
    })
  }
  function resetFields(keyFields: (keyof IFormFiltersVechile)[] | keyof IFormFiltersVechile = []) {
    const arrayFields = Array.isArray(keyFields) ? keyFields : [keyFields];
    arrayFields.forEach((key) => {
      resetField(key)
    })
    resetReducers(arrayFields)
  }
  //#endregion

  //#region Call API Domains
  function callManufacturerApiDomain() {

    if (!isValueQueryParams(watcherCountry) && !isValueQueryParams(watcherManufacturer)) {
      resetFields(['manufacturer', 'model', 'subModel', 'year'])
    }
    const request: ManufacturerRequest = {
      guidCountry: watcherCountry?.key as string
    }
    dispatch(getManufacturers(request, keyReducer));
  }
  function callModelApiDomain(manufacturerSelected: KeyValue | null) {
    if (!isValueQueryParams(manufacturerSelected)) {
      resetFields(['model', 'subModel', 'year'])
    }

    if (isNullUndefined(manufacturerSelected)) return;
    const request: ModelRequest = {
      guidCountry: watcherCountry?.key as string,
      guidManufacturer: manufacturerSelected.key as string
    }
    dispatch(getModels(request, keyReducer));
  }
  function callSubModelApiDomain(modelSelected: KeyValue | null) {
    if (!isValueQueryParams(modelSelected)) {
      resetFields(['subModel', 'year'])
    }
    if (isNullUndefined(watcherManufacturer) || isNullUndefined(modelSelected)) return;
    const request: SubModelRequest = {
      guidCountry: watcherCountry?.key as string,
      guidManufacturer: watcherManufacturer.key as string,
      guidModel: modelSelected.key as string
    }
    dispatch(getSubModels(request, keyReducer));
  }
  function callYearApiDomain(subModelSelected: KeyValue | null) {
    if (!isValueQueryParams(subModelSelected)) {
      resetFields('year')
    }
    if (
      isNullUndefined(watcherManufacturer) ||
      isNullUndefined(watcherModel) ||
      isNullUndefined(subModelSelected)
    ) return;

    const request: VehicleYearRequest = {
      // guidCountry: watcherCountry?.key as string,
      guidManufacturer: watcherManufacturer.key as string,
      guidModel: watcherModel.key as string,
      guidSubModel: subModelSelected.key as string
    }
    dispatch(getYearByManufacturerAndModel(request, keyReducer));
  }
  //#endregion

  useEffect(() => {
    return () => {
      resetReducers(['manufacturer', 'model', 'subModel', 'year'])
    }
  }, [])

  useEffect(() => {
    callManufacturerApiDomain()
  }, [watcherCountry?.key]);

  useEffect(() => {
    callModelApiDomain(watcherManufacturer)
  }, [watcherManufacturer?.key])

  useEffect(() => {
    callSubModelApiDomain(watcherModel)
  }, [watcherModel?.key])

  useEffect(() => {
    callYearApiDomain(watcherSubModel)
  }, [watcherSubModel?.key])


  const disabledManufactures = isNullUndefined(manufacturersReducer.data);
  const disabledModel = isNullUndefined(modelsReducer.data);
  const disabledSubModel = isNullUndefined(subModelsReducer.data);
  const disabledYear = isNullUndefined(yearsReducer.data);

  return (
    <>
      <KogAutoCompleteVehicleControlled
        id="id-mappingCode-select-manufacturer"
        nameCustom="manufacturer"
        label={getTranslateByKey('information.manufacturer')}
        control={control}
        isLoading={manufacturersReducer.isLoading}
        options={manufacturersReducer.data}
        rulesCustom={{ required: isRequiredField('manufacturer') }}
        disabled={disabledManufactures}
        onChangeCustom={(optionSelected: KeyValue | null) => { }}
      />
      <KogAutoCompleteVehicleControlled
        id="id-mappingCode-select-model"
        nameCustom="model"
        label={getTranslateByKey('information.model')}
        control={control}
        isLoading={modelsReducer.isLoading}
        options={modelsReducer.data}
        rulesCustom={{ required: isRequiredField('model') }}
        disabled={disabledModel}
        onChangeCustom={(optionSelected: KeyValue | null) => { }}
      />
      <KogAutoCompleteVehicleControlled
        id="id-mappingCode-select-subModel"
        nameCustom="subModel"
        label={getTranslateByKey('information.subModel')}
        control={control}
        isLoading={subModelsReducer.isLoading}
        options={subModelsReducer.data}
        rulesCustom={{ required: isRequiredField('subModel') }}
        disabled={disabledSubModel}
        onChangeCustom={(optionSelected: KeyValue | null) => { }}
      />
      <KogAutoCompleteControlled
        id="id-mappingCode-select-year"
        nameCustom="year"
        label={getTranslateByKey('information.year')}
        control={control}
        isLoading={yearsReducer.isLoading}
        options={yearsReducer.data}
        rulesCustom={{ required: isRequiredField('year') }}
        disabled={disabledYear}
        onChangeCustom={(optionSelected: KeyValue | null) => { }}
      />
    </>
  )
}

export default FiltersVechile